import { useCallback, useMemo } from 'react';
import { getAllSKUs } from '../services/SKUListAPI';
import { normalizeSku } from '../utils/normalizeSku';
import { useCachedListValidation } from './useCachedListValidation';

interface UseSkuValidationResult {
  allSkus: string[];
  loading: boolean;
  error: string | null;
  validateSku: (sku: string) => boolean;
  findBestSkuMatch: (text: string) => string | null;
  normalizeSku: (sku: string) => string;
}

export function useSkuValidation(): UseSkuValidationResult {
  const { items: allSkus, loading, error, validate } = useCachedListValidation({
    fetchItems: getAllSKUs,
    normalize: normalizeSku,
    label: 'SKUs',
  });

  const skuSet = useMemo(() => new Set(allSkus), [allSkus]);

  const findBestSkuMatch = useCallback(
    (text: string): string | null => {
      if (loading || allSkus.length === 0) return null;
      const normalizedText = normalizeSku(text);
      if (!normalizedText) return null;
      if (skuSet.has(normalizedText)) return normalizedText;

      for (const sku of allSkus) {
        if (
          sku === normalizedText ||
          sku.includes(normalizedText) ||
          normalizedText.includes(sku)
        ) {
          return sku;
        }
      }
      return null;
    },
    [loading, allSkus, skuSet],
  );

  return {
    allSkus,
    loading,
    error,
    validateSku: validate,
    findBestSkuMatch,
    normalizeSku,
  };
}
